import { useCallback } from 'react';
import { useFlagsmithContext } from '../components/FlagsmithProvider';

/**
 * Hook for managing user identification with Flagsmith
 *
 * @returns Functions for identifying users and setting traits
 */
export function useFlagsmithUser() {
  const { identifyUser, setUserTrait, clearUserIdentity } =
    useFlagsmithContext();

  /**
   * Identify a user to Flagsmith
   * @param userId The unique identifier for the user
   */
  const identify = useCallback(
    (userId: string) => {
      identifyUser(userId);
    },
    [identifyUser]
  );

  /**
   * Set a trait for the identified user
   * @param key The trait key (e.g., 'email')
   * @param value The trait value
   */
  const setTrait = useCallback(
    (key: string, value: string | number | boolean) => {
      setUserTrait(key, value);
    },
    [setUserTrait]
  );

  /**
   * Clear the current user identification
   */
  const clearIdentity = useCallback(() => {
    clearUserIdentity();
  }, [clearUserIdentity]);

  /**
   * Identify a user and set multiple traits at once
   * @param userId The unique identifier for the user
   * @param traits Object containing trait key-value pairs
   */
  const identifyWithTraits = useCallback(
    (userId: string, traits: Record<string, string | number | boolean>) => {
      // First identify the user and wait for it to complete
      identifyUser(userId);

      // Use a small timeout to ensure identification completes before setting traits
      // This is a workaround for the debounce mechanism in the identifyUser function
      setTimeout(() => {
        // Set each trait after identification has had time to complete
        Object.entries(traits).forEach(([key, value]) => {
          setUserTrait(key, value);
        });
      }, 600); // Slightly longer than the DEBOUNCE_TIME (500ms) in flagsmith.ts
    },
    [identifyUser, setUserTrait]
  );

  return {
    identify,
    setTrait,
    clearIdentity,
    identifyWithTraits,
  };
}

export default useFlagsmithUser;
